/**
 * WeeklyDigestCard — preview + envoi manuel du digest CFO hebdo.
 */
import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Send, Loader2, Calendar, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function WeeklyDigestCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/legal/weekly-digest/preview"); setData(r.data); }
    catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const sendNow = async () => {
    setSending(true);
    try {
      const r = await api.post("/legal/weekly-digest/send-now");
      if (r.data.sent > 0) toast.success(`Digest envoyé à ${r.data.recipients} destinataire(s)`);
      else toast.info(r.data.message || "Aucun envoi");
    } catch (e) { toast.error(apiError(e)); }
    finally { setSending(false); }
  };

  if (!data) return null;
  const c = data.data?.counts || {};

  return (
    <Card className="border-purple-200" data-testid="weekly-digest-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <Calendar className="w-4 h-4 text-purple-600" />
            Digest hebdo CFO
            <span className="text-xs text-slate-500 font-normal">
              chaque lundi matin
            </span>
          </CardTitle>
          <Button size="sm" onClick={sendNow} disabled={sending || data.recipients_count === 0} data-testid="weekly-digest-send-now">
            {sending ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}
            Envoyer maintenant
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {data.recap && (
          <div className="p-3 rounded bg-gradient-to-r from-indigo-50 to-purple-50 border-l-4 border-purple-500">
            <p className="text-[10px] uppercase tracking-wider font-bold text-purple-700 mb-1">
              <Sparkles className="w-3 h-3 inline mr-0.5" />Résumé IA
            </p>
            <p className="text-sm text-indigo-900 leading-relaxed font-medium">{data.recap}</p>
          </div>
        )}
        <div className="grid grid-cols-6 gap-2 text-xs">
          {[
            ["Échéances 30j", c.upcoming, "text-red-600"],
            ["Assemblées", c.assemblies, "text-purple-600"],
            ["Documents", c.documents, "text-blue-600"],
            ["Dépôts greffe", c.depots, "text-emerald-600"],
            ["Dirigeants+", c.dirigeants_new, "text-amber-600"],
            ["Associés+", c.associes_new, "text-cyan-600"],
          ].map(([label, val, color]) => (
            <div key={label} className="p-2 bg-slate-50 rounded text-center">
              <p className={`font-bold text-lg ${color}`}>{val || 0}</p>
              <p className="text-slate-500 text-[10px] uppercase mt-0.5">{label}</p>
            </div>
          ))}
        </div>
        <p className="text-[11px] text-slate-500">
          {data.recipients_count} destinataire{data.recipients_count > 1 ? "s" : ""} : {data.recipients.join(", ") || "aucun CFO/owner"}
        </p>
      </CardContent>
    </Card>
  );
}
